import { useCallback } from 'react';
import { IAlgorithm } from '../IAlgorithm';
import { Shape, PinPath, Point } from '../Types';
import { squarePoints, ellipsoidPoints } from '../utils/shapeUtils';
import { generatePath } from '../utils/pathUtils';
import { Target } from '../Target';
import { MAX_BATCH_SIZE, TARGET_FRAME_TIME_MS } from '../AppConst';

export async function animRequest(): Promise<void> {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

interface UseStringArtAlgorithmProps {
  updateError: (error: number) => void;
  updateIterationsPerSecond: (iterationsPerSecond: number) => void;
  updateRunningTime: (runningTime: number) => void;
  updateMaxError: (maxError: number) => void;
  updateErrorVelocity: (velocity: number) => void;
  updateTargetImageData?: (targetImageData: ImageData) => void;
  updateCurrentPath?: (path: PinPath) => void;
  updatePoints?: (points: Point[]) => void;
}

interface ErrorHistoryEntry {
  error: number;
  iterationTime: number;
}

const MAX_HISTORY_SIZE = 100;

function computeErrorVelocity(history: ErrorHistoryEntry[]): number {
  if (history.length < 2) {
    return 0;
  }

  const firstEntry = history[0];
  const lastEntry = history[history.length - 1];
  const timeDelta = lastEntry.iterationTime - firstEntry.iterationTime;

  if (timeDelta <= 0 || lastEntry.error <= 0) {
    return 0;
  }

  const errorDelta = firstEntry.error - lastEntry.error;
  return Math.abs((errorDelta / lastEntry.error / timeDelta) * 100);
}

function adjustBatchSize(batchSize: number, frameTime: number): number {
  if (frameTime < TARGET_FRAME_TIME_MS * 0.5) {
    return Math.min(batchSize * 2, MAX_BATCH_SIZE);
  }

  if (frameTime > TARGET_FRAME_TIME_MS * 1.2) {
    return Math.max(Math.floor(batchSize * 0.8), 1);
  }

  return batchSize;
}

export const useStringArtAlgorithm = ({
  updateError,
  updateIterationsPerSecond,
  updateRunningTime,
  updateMaxError,
  updateErrorVelocity,
  updateTargetImageData,
  updateCurrentPath,
  updatePoints,
}: UseStringArtAlgorithmProps) => {
  const runAlgorithm = useCallback(
    async (
      shape: Shape,
      algorithm: IAlgorithm,
      target: Target,
      numberOfPins: number,
      pathLength: number,
    ) => {
      const { width, height } = target;
      const points =
        shape === 'rectangle'
          ? squarePoints(width, height, numberOfPins)
          : ellipsoidPoints([width / 2, height / 2], width / 2, height / 2, numberOfPins);

      updateTargetImageData?.(target.getTargetImageData());
      updatePoints?.(points);

      algorithm.init(points);

      let currentPath = generatePath(points, pathLength);
      let currentDiff = target.computePathDiff(currentPath, points);

      updateCurrentPath?.(currentPath);

      let maxErrorValue = currentDiff;
      updateError(currentDiff);
      updateMaxError(maxErrorValue);

      const errorHistory: ErrorHistoryEntry[] = [{ error: currentDiff, iterationTime: 0 }];

      let batchSize = 1;
      let totalIterationsDone = 0;
      let totalIterationTime = 0;

      while (true) {
        await animRequest();

        const startTime = performance.now();
        const currentBatch = batchSize;

        let candidatePath = currentPath;
        let candidateDiff = currentDiff;

        for (let i = 0; i < currentBatch; i++) {
          [candidatePath, candidateDiff] = algorithm.iterate(candidatePath, candidateDiff);
        }

        const frameTime = performance.now() - startTime;
        totalIterationTime += frameTime;
        totalIterationsDone += currentBatch;

        if (candidateDiff < currentDiff) {
          currentPath = candidatePath;
          currentDiff = candidateDiff;
          updateError(currentDiff);
          updateCurrentPath?.(currentPath);
        }

        maxErrorValue = Math.max(maxErrorValue, currentDiff);
        updateMaxError(maxErrorValue);

        const iterationTimeSeconds = totalIterationTime / 1000;
        errorHistory.push({ error: currentDiff, iterationTime: iterationTimeSeconds });
        if (errorHistory.length > MAX_HISTORY_SIZE) {
          errorHistory.shift();
        }

        const iterationsPerSecond =
          iterationTimeSeconds > 0 ? totalIterationsDone / iterationTimeSeconds : 0;

        updateIterationsPerSecond(iterationsPerSecond);
        updateRunningTime(iterationTimeSeconds);
        updateErrorVelocity(computeErrorVelocity(errorHistory));

        batchSize = adjustBatchSize(batchSize, frameTime);
      }
    },
    [
      updateCurrentPath,
      updateError,
      updateErrorVelocity,
      updateIterationsPerSecond,
      updateMaxError,
      updatePoints,
      updateRunningTime,
      updateTargetImageData,
    ],
  );

  return { runAlgorithm };
};
